import { SymbolFitMode } from "@slot/presentation";

/**
 * Per-game reel SYMBOL CATALOG — config VALUES (migrated out of the scene-authored
 * SymbolCatalogComponent, 2026-06). No asset references here: only atlas FRAME NAMES, resolved at
 * runtime against the injected symbols atlas by `RuntimeSymbolCatalog.fromConfig`. Edit symbols
 * HERE, not in the scene.
 *
 * `blurredFrameName` (the `<id>-b` atlas frame) gives the reel drum's filler scroll its baked-in
 * motion blur. The `-b` frame fits the cell by its own aspect (different from the crisp), so
 * `blurredScale`/`blurredOffset*` tune it to match the crisp on-screen: baseScale ×= blurredScale,
 * offset += blurredOffset. Omitted `blurredFrameName` ⇒ that symbol scrolls crisp.
 */
export interface SymbolCatalogEntry {
  /** Logical symbol id (matches the server/fixture grid + the reel finals). */
  symbolId: string;
  /** Crisp atlas frame name. */
  frameName: string;
  /** Scale multiplier applied to the fitted sprite. */
  baseScale: number;
  /** How the sprite fits its cell. */
  fitMode: SymbolFitMode;
  /** Per-symbol pixel offset (default 0). */
  offsetX?: number;
  offsetY?: number;
  /** Land-sound key (default none). */
  landSoundKey?: string;
  /** With a `landSoundKey` set: suppress the default `column_drop` thud (default
   *  true — the land sound replaces it) or set false to play BOTH together.
   *  Ignored when there's no `landSoundKey` (the thud always plays then). */
  suppressDefaultDropSound?: boolean;
  /** Pre-blurred FILLER atlas frame (default none ⇒ crisp filler). */
  blurredFrameName?: string;
  /** Scale MULTIPLIER on the blurred frame (× baseScale; default 1). */
  blurredScale?: number;
  /** Extra offset (px) ADDED for the blurred frame (default 0). */
  blurredOffsetX?: number;
  blurredOffsetY?: number;
}

export const SYMBOL_CATALOG: readonly SymbolCatalogEntry[] = [
  // High symbols: Contain fit, slightly upscaled.
  { symbolId: "H4", frameName: "H4", baseScale: 1.1, fitMode: SymbolFitMode.Contain, blurredFrameName: "H4-b", blurredScale: 1.2 },
  { symbolId: "H3", frameName: "H3", baseScale: 1.1, fitMode: SymbolFitMode.Contain, blurredFrameName: "H3-b", blurredScale: 1.2 },
  { symbolId: "H2", frameName: "H2", baseScale: 1.1, fitMode: SymbolFitMode.Contain, blurredFrameName: "H2-b", blurredScale: 1.2 },
  { symbolId: "H1", frameName: "H1", baseScale: 1.1, fitMode: SymbolFitMode.Contain, blurredFrameName: "H1-b", blurredScale: 1.2 },
  // Low symbols: slightly downscaled. L5 fits by HEIGHT; the rest Contain.
  { symbolId: "L5", frameName: "L5", baseScale: 0.9, fitMode: SymbolFitMode.MatchHeight, blurredFrameName: "L5-b", blurredScale: 1.2 },
  { symbolId: "L4", frameName: "L4", baseScale: 0.9, fitMode: SymbolFitMode.Contain, blurredFrameName: "L4-b", blurredScale: 1.2 },
  { symbolId: "L3", frameName: "L3", baseScale: 0.9, fitMode: SymbolFitMode.Contain, blurredFrameName: "L3-b", blurredScale: 1.2 },
  { symbolId: "L2", frameName: "L2", baseScale: 0.9, fitMode: SymbolFitMode.Contain, blurredFrameName: "L2-b", blurredScale: 1.2 },
  { symbolId: "L1", frameName: "L1", baseScale: 1, fitMode: SymbolFitMode.Contain, blurredFrameName: "L1-b", blurredScale: 1.2 },
  // Specials: the scatter (FS), the VS battle wild, the wild (W). FS and VS land with their own sound on top of the thud.
  { symbolId: "FS", frameName: "FS", baseScale: 1, fitMode: SymbolFitMode.Contain, blurredFrameName: "FS-b", blurredScale: 1.2, landSoundKey: "grid_scatter_drop", suppressDefaultDropSound: false },
  { symbolId: "VS", frameName: "VS", baseScale: 1, fitMode: SymbolFitMode.Contain, blurredFrameName: "VS-b", blurredScale: 1.2, landSoundKey: "column_vs_drop", suppressDefaultDropSound: false },
  { symbolId: "W", frameName: "W", baseScale: 1, fitMode: SymbolFitMode.Contain, blurredFrameName: "W-b", blurredScale: 1.2 },
];

/**
 * The drum's filler strip when the grid on screen is too uniform to read as a spin
 * (`ReelSpinMechanicDeps.fillerSymbolIds`): the six plain symbols the mechanic used to hard-code.
 */
export const REEL_FILLER_SYMBOL_IDS: readonly string[] = ["H1", "H2", "H3", "L1", "L2", "L3"];
